import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, Link, Paper, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {
    DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAuth } from '../../../context/AuthContext';
import { useToast } from '../../../context/ToastContext';
import { DashboardStyleGroup } from '../../../context/DashboardStyleContext';
import { PERMISSIONS } from '../../../constants/permissions';
import { AuthService } from '../../../services/auth.service';
import { isWidgetVisible } from '../registry';
import { homeFallbackFor, stripPresetFor, PresetKey } from '../registry/presets';
import { isGatedModule } from '../registry/moduleMeta';
import { useDashboardAccess } from '../useDashboardAccess';
import useDashboardLayout from '../board/useDashboardLayout';
import WidgetCard from '../board/WidgetCard';
import AddWidgetDialog from '../board/AddWidgetDialog';
import MerchStatRow from './MerchStatRow';
import RoleLensBoard from './RoleLensBoard';
import { ThreadLensKey } from '../../../interfaces/threadLens.interface';
import NeedsActionPanel from './NeedsActionPanel';
import { OrderThreadsList, useLeadOutputs } from './OrderThreadsPanel';
import { ShipThisWeekTable } from './ShipThisWeekPanel';
import { useThreadOrders } from './useThreadData';

// A denser grid than the Tiles board: four small cards or three medium ones per row on wide screens.
const SPAN: Record<string, number> = { sm: 3, md: 4, lg: 6 };

// The board key is its OWN ('home-thread'): the Tiles board's saved 'home' layout is never read or written here.
const THREAD_BOARD_KEY = 'home-thread';

function SortableWidget({ id, children }: { id: string; children: React.ReactNode }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    return (
        <Box
            ref={setNodeRef}
            {...attributes}
            {...listeners}
            sx={{ height: '100%' }}
            style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 }}
        >
            {children}
        </Box>
    );
}

function Section({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
    return (
        <Paper variant="outlined" sx={{ p: 2, minWidth: 0, height: '100%' }}>
            <Typography variant="subtitle2" fontWeight={700}>{title}</Typography>
            {subtitle && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>{subtitle}</Typography>}
            {children}
        </Paper>
    );
}

/** Registry widgets for the people whose home is the whole-business board, on their own 'home-thread' layout. */
function ThreadWidgetGrid() {
    const { hasPermission } = useAuth();
    const toast = useToast();
    const institutionId = AuthService.getSelectedInstitution();
    const { primary, enabledModules } = useDashboardAccess();
    const { order, widgets, locked, add, remove, reorder, reset } = useDashboardLayout({
        institutionId, boardKey: THREAD_BOARD_KEY, fallbackIds: homeFallbackFor(primary, hasPermission), editable: true,
        onError: e => toast.showApiError?.(e, 'Could not save the dashboard'),
    });
    const visibleWidgets = useMemo(
        () => widgets.filter(def => (!isGatedModule(def.module) || enabledModules.has(def.module)) && isWidgetVisible(def, hasPermission)),
        [widgets, enabledModules, hasPermission],
    );
    const [addOpen, setAddOpen] = useState(false);
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

    const onDragEnd = (e: DragEndEvent) => {
        if (!e.over || e.active.id === e.over.id) return;
        const from = order.indexOf(String(e.active.id));
        const to = order.indexOf(String(e.over.id));
        if (from >= 0 && to >= 0) reorder(from, to);
    };

    return (
        <Box data-help-section="dashboard.thread.widgets">
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ flexGrow: 1 }}>Your widgets</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={() => setAddOpen(true)} data-help="dashboard.addWidget">Add widget</Button>
                <Button size="small" startIcon={<RestartAltIcon />} onClick={reset} data-help="dashboard.reset">Reset to default</Button>
            </Stack>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                <SortableContext items={visibleWidgets.map(w => w.id)} strategy={rectSortingStrategy}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 1.5 }}>
                        {visibleWidgets.map(def => (
                            <Box key={def.id} sx={{ gridColumn: { xs: 'span 12', sm: 'span 6', md: `span ${SPAN[def.size]}` }, minWidth: 0 }}>
                                <SortableWidget id={def.id}>
                                    <WidgetCard def={def} institutionId={institutionId} editable locked={locked.includes(def.id)} onRemove={() => remove(def.id)} />
                                </SortableWidget>
                            </Box>
                        ))}
                    </Box>
                </SortableContext>
            </DndContext>
            <AddWidgetDialog
                open={addOpen}
                hasPermission={hasPermission}
                present={order}
                enabledModules={Array.from(enabledModules)}
                onAdd={add}
                onClose={() => setAddOpen(false)}
            />
        </Box>
    );
}

/** The owner lens covers the admin wildcard and the 'basic' preset; every other preset has its own lens. */
const lensFor = (preset: PresetKey): ThreadLensKey => (preset === 'basic' ? 'owner' : preset);

/**
 * The Thread style of the home dashboard: a dense stat row, a task list, the order threads as the hero,
 * the week's shipments, and (for the whole-business board) the person's widgets in a tighter grid.
 * Each section shows only when the viewer holds its key. Nothing here writes the Tiles board's layout.
 */
export default function ThreadDashboardBoard({ group }: { group: Extract<DashboardStyleGroup, 'board' | 'merch'> }) {
    const { hasPermission } = useAuth();
    const { primary, enabledModules } = useDashboardAccess();
    const institutionId = AuthService.getSelectedInstitution();

    const canThreads = hasPermission(PERMISSIONS.MERCH_REPORT_VIEW) && enabledModules.has('merchandising');
    const orders = useThreadOrders(institutionId, canThreads);
    const leadOutputs = useLeadOutputs(institutionId, orders.rows);
    const stripPreset = stripPresetFor(primary, hasPermission);

    return (
        <Box sx={{ minWidth: 0 }} data-help-page="dashboard.thread.page">
            {group === 'board' ? (
                <RoleLensBoard institutionId={institutionId} lens={lensFor(stripPreset)} />
            ) : (
                <>
                    {canThreads && <MerchStatRow institutionId={institutionId} rows={orders.rows} loading={orders.loading} truncated={orders.truncated} />}
                    {canThreads && orders.rows !== null && <NeedsActionPanel rows={orders.rows} />}
                </>
            )}

            {canThreads ? (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 1.5, mb: 2 }}>
                    <Box sx={{ gridColumn: { xs: 'span 12', lg: 'span 8' }, minWidth: 0 }} data-help-section="dashboard.thread.orders">
                        <Section title="Order threads" subtitle="Live orders, late first. Open one to see its steps.">
                            <OrderThreadsList rows={orders.rows} loading={orders.loading} error={orders.error} truncated={orders.truncated} leadOutputs={leadOutputs} showHero />
                        </Section>
                    </Box>
                    <Box sx={{ gridColumn: { xs: 'span 12', lg: 'span 4' }, minWidth: 0 }} data-help-section="dashboard.thread.ship">
                        <Section title="Ship this week" subtitle="Next 7 days, soonest first">
                            <ShipThisWeekTable rows={orders.rows} loading={orders.loading} error={orders.error} />
                        </Section>
                    </Box>
                </Box>
            ) : group === 'merch' ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Order threads need the merchandising report access. Ask an admin to add it to your role, or open{' '}
                    <Link component={RouterLink} to="/merchandising/orders">your order list</Link>.
                    You can switch to Tiles at the top of this page.
                </Alert>
            ) : null}

            {group === 'board' && <ThreadWidgetGrid />}
        </Box>
    );
}
